import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@heidi/design-system/hover-card';
import {
  StatusIndicator,
  type StatusIndicatorStatus,
} from '@heidi/design-system/status-indicator';
import {
  TypographyCaption,
  TypographyP2,
  typographyVariant,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { useId, useRef, useState, type ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { OBJECT_KIND_ICON, PatientAvatar } from './ChatTokenChip';
import {
  findMetadataField,
  formatObjectReferenceMetadata,
  type ObjectReferenceMetadataField,
  type ObjectReferenceSummary,
} from './object-reference-metadata';

export type {
  ObjectReferenceMetadataField,
  ObjectReferenceSummary,
} from './object-reference-metadata';

const OBJECT_REFERENCE_SUMMARY_CLASS =
  'w-[300px] max-w-[calc(100vw-24px)] rounded-lg border border-border bg-surface-elevated px-3.5 py-3 text-text-primary shadow-sm ring-0';

const TASK_STATUS_INDICATOR = {
  todo: 'to-do',
  in_progress: 'in-progress',
  done: 'done',
  archived: 'archive',
} as const satisfies Record<
  NonNullable<Extract<ObjectReferenceMetadataField, { kind: 'taskStatus' }>['status']>,
  StatusIndicatorStatus
>;

function linkedSessionPatientName(object: ObjectReferenceSummary): string | undefined {
  if (object.kind !== 'session') return undefined;
  const link = findMetadataField(object, 'patientLink');
  if (!link || link.status === 'unlinked') return undefined;
  return link.patientName?.trim() || undefined;
}

function ObjectReferenceMarker({
  object,
}: {
  readonly object: ObjectReferenceSummary;
}): JSX.Element {
  const avatarName =
    object.kind === 'patient' ? object.title : linkedSessionPatientName(object);
  const taskStatus =
    object.kind === 'task'
      ? findMetadataField(object, 'taskStatus')?.status || undefined
      : undefined;
  const Icon = OBJECT_KIND_ICON[object.kind];
  return (
    <span className="flex h-5 w-4.5 shrink-0 items-center justify-center">
      {avatarName ? (
        <PatientAvatar name={avatarName} avatarSeed={object.avatarSeed} />
      ) : taskStatus ? (
        <StatusIndicator
          status={TASK_STATUS_INDICATOR[taskStatus]}
          size="xs"
          aria-hidden="true"
          data-task-status={taskStatus}
        />
      ) : (
        <Icon
          aria-hidden="true"
          className="size-4 text-text-secondary"
          strokeWidth={1.5}
        />
      )}
    </span>
  );
}

function ObjectReferenceSummaryContent({
  object,
}: {
  readonly object: ObjectReferenceSummary;
}): JSX.Element {
  const intl = useIntl();
  const metadata = formatObjectReferenceMetadata(object, intl);
  const summary = object.summary?.trim();
  return (
    <div className="flex flex-col gap-1.5" data-object-kind={object.kind}>
      <div className="flex min-w-0 items-start gap-2">
        <ObjectReferenceMarker object={object} />
        <span
          className={typographyVariant({
            variant: 'p2Bold',
            className:
              'line-clamp-2 min-w-0 whitespace-normal break-words text-text-primary',
          })}
        >
          {object.title}
          {object.titleDetail ? (
            <span className="font-normal text-text-secondary">
              {' '}
              · {object.titleDetail}
            </span>
          ) : null}
        </span>
      </div>
      {summary ? (
        <TypographyP2 className="line-clamp-3 whitespace-normal break-words text-[13px] leading-[18px] text-text-primary">
          {summary}
        </TypographyP2>
      ) : null}
      {metadata.length > 0 ? (
        <TypographyCaption className="whitespace-normal break-words text-text-tertiary">
          {metadata.join(' · ')}
        </TypographyCaption>
      ) : null}
    </div>
  );
}

export function ObjectReferenceHoverCard({
  object,
  children,
  clickable = true,
}: {
  readonly object: ObjectReferenceSummary;
  readonly children: ReactElement;
  // Without it the trigger adds no click handler, so a chip that opens nothing stays a span.
  readonly clickable?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  // Clicking opens a panel that moves the token, so the card closes without its exit animation.
  const [dismissed, setDismissed] = useState(false);
  const summaryId = useId();
  // A click also focuses the trigger, and Radix's delayed focus-open would reopen the card.
  const activated = useRef(false);
  const release = () => {
    activated.current = false;
    setDismissed(false);
  };
  return (
    <HoverCard
      open={open}
      onOpenChange={(next) => {
        if (!next || !activated.current) setOpen(next);
      }}
      openDelay={300}
      closeDelay={150}
    >
      <HoverCardTrigger
        asChild
        aria-describedby={open ? summaryId : undefined}
        onClick={
          clickable
            ? () => {
                activated.current = true;
                setDismissed(true);
                setOpen(false);
              }
            : undefined
        }
        onPointerLeave={release}
        onBlur={release}
      >
        {children}
      </HoverCardTrigger>
      <HoverCardContent
        id={summaryId}
        align="start"
        side="top"
        sideOffset={8}
        className={cn(
          OBJECT_REFERENCE_SUMMARY_CLASS,
          dismissed && 'data-[state=closed]:!animate-none'
        )}
        data-testid="object-reference-hover-card"
      >
        <ObjectReferenceSummaryContent object={object} />
      </HoverCardContent>
    </HoverCard>
  );
}
